import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { Badge, Box, Chip, Stack, Typography } from '@mui/material';
import BotAvatar from './BotAvatar';

const LINE = 'rgba(15, 23, 42, 0.25)';
const GAP = 20;
const NODE_WIDTH = 188;
const SIBLING_PAD = 16;
const ROOT_GAP = 32;

/** Natural width of a branch, from the tree shape (no DOM measuring, so it does not depend on how a browser reports zoom). */
function branchWidth(bot, childrenOf) {
  const kids = childrenOf.get(bot.id) || [];
  if (!kids.length) return NODE_WIDTH;
  return Math.max(NODE_WIDTH, kids.reduce((sum, k) => sum + branchWidth(k, childrenOf) + SIBLING_PAD, 0));
}

function descendantIds(id, childrenOf, acc = new Set()) {
  (childrenOf.get(id) || []).forEach((k) => { acc.add(k.id); descendantIds(k.id, childrenOf, acc); });
  return acc;
}

function NodeCard({
  bot, counts, selected, onSelect, dnd,
}) {
  const open = counts?.open || 0;
  const overdue = counts?.overdue || 0;
  const draggable = Boolean(dnd?.canDrag(bot));
  const dropOk = Boolean(dnd?.canDrop(bot));
  const hot = dnd?.overId === bot.id && dropOk;
  return (
    <Box
      component="div"
      role="button"
      tabIndex={0}
      onClick={() => onSelect(bot)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(bot); } }}
      aria-label={`${bot.name}, ${bot.role}`}
      draggable={draggable}
      onDragStart={draggable ? (e) => dnd.start(e, bot) : undefined}
      onDragEnd={draggable ? dnd.end : undefined}
      onDragOver={dropOk ? (e) => { e.preventDefault(); dnd.over(bot.id); } : undefined}
      onDragLeave={dropOk ? () => dnd.over(null) : undefined}
      onDrop={dropOk ? (e) => { e.preventDefault(); dnd.drop(bot.id); } : undefined}
      sx={{
        userSelect: 'none',
        cursor: draggable ? 'grab' : 'pointer',
        boxSizing: 'border-box',
        width: 188,
        p: 1.25,
        textAlign: 'center',
        bgcolor: 'background.paper',
        border: 2,
        borderColor: hot ? 'success.main' : selected ? 'primary.main' : 'divider',
        borderRadius: 3,
        boxShadow: hot ? '0 0 0 4px rgba(46,125,50,0.25)' : selected ? '0 0 0 3px rgba(25,118,210,0.15)' : '0 1px 3px rgba(15,23,42,0.08)',
        opacity: dnd?.dragId === bot.id ? 0.4 : bot.active ? 1 : 0.6,
        '&:hover': { borderColor: 'primary.main' },
        '&:focus-visible': { outline: '3px solid rgba(25,118,210,0.5)' },
      }}
    >
      <Stack alignItems="center" spacing={0.5}>
        <Badge color={overdue ? 'error' : 'primary'} badgeContent={open} invisible={!open} max={99}>
          <BotAvatar bot={bot} size={44} />
        </Badge>
        <Typography fontWeight={600} noWrap sx={{ maxWidth: '100%' }}>{bot.name}</Typography>
        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '100%' }}>{bot.role}</Typography>
        <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" useFlexGap>
          {bot.is_primary && <Chip size="small" color="primary" variant="outlined" label="Primary" />}
          {!bot.active && <Chip size="small" label="Inactive" />}
          {overdue > 0 && <Chip size="small" color="error" label={`${overdue} overdue`} />}
          {bot.permissions.length > 0 && <Chip size="small" variant="outlined" label={`${bot.permissions.length} access`} />}
        </Stack>
      </Stack>
    </Box>
  );
}

function Branch({
  bot, childrenOf, counts, selectedId, onSelect, dnd,
}) {
  const kids = childrenOf.get(bot.id) || [];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <NodeCard bot={bot} counts={counts[bot.id]} selected={selectedId === bot.id} onSelect={onSelect} dnd={dnd} />
      {kids.length > 0 && (
        <>
          <Box sx={{ width: 2, height: GAP, bgcolor: LINE }} />
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            {kids.map((k, i) => (
              <Box
                key={k.id}
                sx={{
                  position: 'relative',
                  px: 1,
                  pt: `${GAP}px`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  // horizontal bar across the siblings, trimmed at the first and last child
                  '&::before': {
                    content: '""', position: 'absolute', top: 0, height: 2, bgcolor: LINE, left: i === 0 ? '50%' : 0, right: i === kids.length - 1 ? '50%' : 0,
                  },
                  // short vertical line down to the child
                  '&::after': {
                    content: '""', position: 'absolute', top: 0, left: 'calc(50% - 1px)', width: 2, height: GAP, bgcolor: LINE,
                  },
                }}
              >
                <Branch bot={k} childrenOf={childrenOf} counts={counts} selectedId={selectedId} onSelect={onSelect} dnd={dnd} />
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

/**
 * The reporting hierarchy as a connected tree. A badge on each member shows open tasks (red when something is overdue).
 * Click a member to open their details. The chart shrinks to fit the page width; `scale` is the user's own zoom on top.
 * When `onMove` is given, members can be dragged onto another member (to report to them) or onto the Unassigned tray
 * (to sit outside the chart). The primary bot cannot be moved, and a member cannot be dropped under their own team.
 */
export default function OrgChart({
  bots, counts = {}, selectedId, onSelect, scale = 1, onMove = null,
}) {
  const outer = useRef(null);
  const [fit, setFit] = useState(1);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const { roots, childrenOf, unassigned } = useMemo(() => {
    const map = new Map();
    bots.forEach((b) => {
      if (b.reports_to_bot_id) map.set(b.reports_to_bot_id, [...(map.get(b.reports_to_bot_id) || []), b]);
    });
    const hasPrimary = bots.some((b) => b.is_primary);
    const orphan = (b) => !b.reports_to_bot_id || !bots.some((x) => x.id === b.reports_to_bot_id);
    const top = bots.filter((b) => orphan(b) && (!hasPrimary || b.is_primary));
    const loose = hasPrimary ? bots.filter((b) => orphan(b) && !b.is_primary) : [];
    return { roots: top, childrenOf: map, unassigned: loose };
  }, [bots]);

  const dragged = dragId ? bots.find((b) => b.id === dragId) : null;
  const blocked = useMemo(() => (dragId ? descendantIds(dragId, childrenOf) : new Set()), [dragId, childrenOf]);
  const dnd = onMove ? {
    dragId,
    overId,
    canDrag: (b) => !b.is_primary,
    canDrop: (b) => Boolean(dragged) && b.id !== dragged.id && b.active && !blocked.has(b.id) && dragged.reports_to_bot_id !== b.id,
    start: (e, b) => {
      e.dataTransfer.effectAllowed = 'move';
      try { e.dataTransfer.setData('text/plain', b.id); } catch (err) { /* some browsers refuse; the id is kept in state */ }
      setDragId(b.id);
    },
    end: () => { setDragId(null); setOverId(null); },
    over: setOverId,
    drop: (targetId) => {
      const moving = dragged;
      setDragId(null);
      setOverId(null);
      if (moving) onMove(moving, targetId);
    },
  } : null;
  const trayOpen = Boolean(dnd && dragged && dragged.reports_to_bot_id);

  const natural = useMemo(
    () => roots.reduce((sum, r) => sum + branchWidth(r, childrenOf), 0) + ROOT_GAP * Math.max(roots.length - 1, 0),
    [roots, childrenOf],
  );

  const measure = useCallback(() => {
    const room = (outer.current?.clientWidth || 0) - 48;
    if (natural > 0 && room > 0) setFit(Math.max(0.5, Math.min(1, room / natural)));
  }, [natural]);

  useLayoutEffect(measure, [measure]);
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !outer.current) return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(outer.current);
    return () => observer.disconnect();
  }, [measure]);

  const zoom = fit * scale;
  return (
    <Box ref={outer} sx={{ overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper', p: 3 }} data-testid="org-chart">
      <Box sx={{ display: 'flex', justifyContent: 'center', zoom, minWidth: 'max-content' }}>
        <Stack direction="row" spacing={4} alignItems="flex-start">
          {roots.map((r) => (
            <Branch key={r.id} bot={r} childrenOf={childrenOf} counts={counts} selectedId={selectedId} onSelect={onSelect} dnd={dnd} />
          ))}
        </Stack>
      </Box>
      {(unassigned.length > 0 || trayOpen) && (
        <Box
          data-testid="unassigned-tray"
          onDragOver={trayOpen ? (e) => { e.preventDefault(); setOverId('tray'); } : undefined}
          onDragLeave={dnd ? () => setOverId(null) : undefined}
          onDrop={trayOpen ? (e) => { e.preventDefault(); dnd.drop(null); } : undefined}
          sx={{
            mt: 3,
            p: 2,
            border: 2,
            borderStyle: 'dashed',
            borderRadius: 3,
            borderColor: overId === 'tray' ? 'success.main' : 'divider',
            bgcolor: overId === 'tray' ? 'rgba(46,125,50,0.06)' : 'action.hover',
          }}
        >
          <Typography fontWeight={600}>Unassigned</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: unassigned.length ? 1.5 : 0 }}>
            {unassigned.length
              ? 'These members are not in the reporting chart yet. Drag one onto a manager to place them. OrkaCrew can still message them.'
              : 'Drop a member here to take them out of the chart.'}
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {unassigned.map((b) => (
              <NodeCard key={b.id} bot={b} counts={counts[b.id]} selected={selectedId === b.id} onSelect={onSelect} dnd={dnd} />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
